<!-- @format -->
<script>
import { debounce as lodashDebounce } from "lodash";

export default {
  props: {
    id: { type: String, required: true },
    label: { type: String, required: true },
    placeholder: { type: String, default: "" },
    value: { type: [String, Number], default: "" },
    readonly: { type: Boolean, default: false },
    debounce: { type: Number, default: 0 },
    autocomplete: { type: String, required: false },
    spellcheck: { type: Boolean, required: false },
    error: { type: Boolean, default: false },
    errorMessage: { type: String, default: "" },
    activeClick: { type: Boolean, default: false },
    activeFocus: { type: Boolean, default: false },
    selectOnFocus: { type: Boolean, default: false },
  },
  data() {
    return {
      internalValue: this.value,
      emitChange: null,
    };
  },
  watch: {
    value(val) {
      this.internalValue = val;
    },
  },
  mounted() {
    if (this.debounce > 0) {
      this.emitChange = lodashDebounce((val) => {
        this.$emit("input", val);
      }, this.debounce);
    } else {
      this.emitChange = (val) => this.$emit("input", val);
    }
  },
  methods: {
    handleInput(event) {
      this.internalValue = event.target.value;
      this.emitChange(event.target.value);
    },
    focus(event) {
      const input = this.$el.querySelector("input");
      if (!input) return;

      if (event) {
        const isInput = event.target === input;
        const isLabel = event.target.tagName === 'LABEL' && event.target.getAttribute('for') === this.id;
        const isAbsolute = window.getComputedStyle(event.target).position === 'absolute';

        if (isInput || isLabel || isAbsolute) return;
      }

      const shouldClick = this.activeClick;
      const shouldFocus = this.activeFocus;

      if (shouldClick) input.click();
      if (shouldFocus) input.focus();
    },
    handleFocus(event) {
      this.$emit('focus', event);
      if (this.selectOnFocus) {
        event.target.select();
      }
    },
    handleClick(event) {
      this.$emit('click', event);
      if (this.selectOnFocus) {
        event.target.select();
      }
    },
  },
};
</script>

<template>
  <div class="field" :class="{ 'field--readonly': readonly, 'field--error': error }" @click="focus($event)">
    <div class="field__icon" v-if="$slots.icon">
      <slot name="icon"></slot>
    </div>

    <div class="field__data">
      <label :for="id" class="field__label font-12 font-500 font-strong">
        {{ label }}
      </label>

      <input :id="id" class="field__input font-16 font-500 font-main" type="text" :placeholder="placeholder"
        :value="internalValue" :readonly="readonly" @input="handleInput" @focus="handleFocus"
        @keydown="$emit('keydown', $event)" @click="handleClick" :autocomplete="autocomplete"
        :spellcheck="spellcheck" />
      <small v-if="error && errorMessage" class="field__error">{{ errorMessage }}</small>
    </div>
  </div>
</template>
<style scoped lang="scss">
.field {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  width: 100%;
  height: 52px;
  padding: 0px  var(--space-12);
  border: 1px solid var(--border-strong);
  border-radius: var(--space-8);
  background-color: var(--bg-base);
  transition: box-shadow 0.2s, background-color 0.2s;

  &:hover {
    border: 1px solid var(--border-strong-hover)
  }

  label {
    margin: 0px;
    pointer-events: none;
  }

  .icon {
    display: flex;
  }

  &--readonly {
    cursor: pointer;
  }

  &--readonly &__input {
    cursor: pointer;
    user-select: none;
  }

  &--readonly:hover {
    background-color: var(--bg-level-2);
  }

  &--error {
    border-color: var(--text-error);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    span {
      display: flex;
    }
  }

  &__data {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  &__label {
    padding-top: calc(var(--space-4) * 1.5);
  }

  &__input {
    border: none;
    background: transparent;
    outline: none;
    width: 100%;
    text-overflow: ellipsis;
    padding: 0px ;
    height: 26px;

    &::placeholder {
      font-size: 0.875rem;
      font-weight: 400;
      color: var(--text-disabled);
    }
  }

  &__error {
    position: absolute;
    bottom: -15px;
    left: 3px;
    color: var(--text-error);
    font-size: 0.75rem;
    line-height: 1.2;
    cursor: text;
  }
}
</style>
